import { countClass, currentUser, deleteClassRecord, loadCurrentUserFromSession, queryAllClass, queryClass, requestParseApi, runCloud, saveClassRecord, type ParseRecord, type QueryClassOptions } from './parse';

// This allowlist is deliberately local and explicit. A route can never turn a
// database class name from a URL or a saved config object into an API target.
export const ADMIN_CLASSES = {
  course: 'elearning_Course',
  courseFolder: 'elearning_CourseFolder',
  subject: 'elearning_Subjects',
  video: 'elearning_Videos',
  material: 'elearning_CourseMaterial',
  customer: 'elearning_Customer',
  subscription: 'elearning_Subscriptions',
  miscCategory: 'elearning_MiscellCategories',
  banner: 'elearning_Banners',
  faq: 'elearning_FAQ',
  about: 'elearning_AboutUs',
  terms: 'elearning_TermsAndConditions',
  query: 'elearning_AskQueries',
  story: 'elearning_Story',
  socialLinks: 'elearning_SocialLinks',
  coupon: 'elearning_Coupon',
  notification: 'elearning_Notifications',
  softConfig: 'elearning_SoftConfig',
  buildConfig: 'elearning_BuildConfig',
  test: 'test_TestInfo',
  question: 'test_TestQuestions',
  result: 'test_TestResult',
} as const;

const allowedClasses = new Set<string>(Object.values(ADMIN_CLASSES));
const adminRoleKey = 'ayurguide-v3-admin-role';
const acceptedAdminRoles = new Set(['elearning_owner', 'elearning_support', 'elearning_appeditor', 'elearning_premium', 'elearning_uat owner', 'ayurguide_owner', 'ayurguide_support', 'ayurguide_appeditor']);

function extractRoles(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((entry) => {
    if (typeof entry === 'string') return [entry.trim().toLowerCase()];
    if (entry && typeof entry === 'object') {
      const record = entry as { role?: unknown; name?: unknown; roleName?: unknown; result?: unknown; roles?: unknown; groups?: unknown };
      if (record.result !== undefined) return extractRoles(record.result);
      if (record.roles !== undefined) return extractRoles(record.roles);
      if (record.groups !== undefined) return extractRoles(record.groups);
      return [record.role, record.name, record.roleName].filter((candidate): candidate is string => typeof candidate === 'string').map((candidate) => candidate.trim().toLowerCase());
    }
    return [];
  });
}

export function assertAdminClass(className: string) {
  if (!allowedClasses.has(className)) throw new Error('This admin resource is not enabled for AyurGuide.');
}

export function isAdminSessionActive() {
  return Boolean(currentUser() && sessionStorage.getItem('ayurguide-v3-session') && sessionStorage.getItem(adminRoleKey));
}

export async function verifyAdminAccess() {
  const sessionToken = sessionStorage.getItem('ayurguide-v3-session');
  if (!sessionToken || !(await loadCurrentUserFromSession())) return false;
  try {
    // The deployed UserGroups cloud function is legacy code and reads the
    // custom `sessiontoken` header instead of X-Parse-Session-Token.
    const groups = await runCloud<unknown>('UserGroups', { appname: 'elearning' }, { headers: { sessiontoken: sessionToken } });
    const role = extractRoles(groups).find((candidate) => acceptedAdminRoles.has(candidate));
    if (!role) return false;
    sessionStorage.setItem(adminRoleKey, role);
    return true;
  } catch {
    return false;
  }
}

export async function queryAdmin(className: string, keys: string[], options: Parameters<typeof queryClass>[1] = {}) {
  assertAdminClass(className);
  return queryClass(className, { ...options, appScoped: true, keys });
}

export async function queryAllAdmin(className: string, keys: string[], options: QueryClassOptions = {}) {
  assertAdminClass(className);
  return queryAllClass(className, { ...options, appScoped: true, keys });
}

export async function countAdmin(className: string, options: Parameters<typeof countClass>[1] = {}) {
  assertAdminClass(className);
  return countClass(className, { ...options, appScoped: true });
}

export async function saveAdmin(className: string, data: Record<string, unknown>, objectId?: string) {
  assertAdminClass(className);
  if (!isAdminSessionActive()) throw new Error('Sign in with an AyurGuide owner account before saving.');
  return saveClassRecord(className, data, objectId);
}

export async function registerAppUser(data: { First_name: string; Email_id: string; Mob_no: string; objectId?: string }) {
  if (!isAdminSessionActive()) throw new Error('Sign in with an AyurGuide owner account before saving.');
  return runCloud<{ objectId: string; UserId: { __type: 'Pointer'; className: '_User'; objectId: string } }>('AdminRegisterAppUser', data);
}

export async function deleteAdmin(className: string, objectId: string) {
  assertAdminClass(className);
  if (!isAdminSessionActive()) throw new Error('Sign in with an AyurGuide owner account before deleting.');
  return deleteClassRecord(className, objectId);
}

export type VideoFolderRecord = { objectId: string; FolderName: string; videoCount?: number };

export async function listVideoFolders(courseId: string) {
  const params = new URLSearchParams({ courseId });
  return requestParseApi<{ results: VideoFolderRecord[] }>(`/video-folders?${params.toString()}`);
}

export async function saveVideoFolder(courseId: string, FolderName: string, objectId?: string) {
  return requestParseApi<VideoFolderRecord>(objectId ? `/video-folders/${encodeURIComponent(objectId)}` : '/video-folders', {
    method: objectId ? 'PUT' : 'POST',
    body: JSON.stringify(objectId ? { FolderName } : { courseId, FolderName }),
  });
}

export async function deleteVideoFolder(objectId: string) {
  return requestParseApi<{ objectId: string; deleted: boolean }>(`/video-folders/${encodeURIComponent(objectId)}`, { method: 'DELETE' });
}

export function safeObjectId(row: ParseRecord | null | undefined) {
  return row?.objectId || '';
}
